import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import fileDownload from "js-file-download";
import { Button, Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { ClipLoader } from "react-spinners";

import api from "services/api";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import PageHero, {
  PageHeroExportButton,
  PageHeroRefreshButton,
} from "components/PageHero";
import ShellCard from "components/ShellCard";
import MetricCardGroup from "components/MetricCardGroup";
import MetricCard from "components/MetricCard";
import CustomTable from "components/CustomTable";
import SpecificEmpresaRequiredNotice from "components/SpecificEmpresaRequiredNotice";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import {
  fmtBRL,
  fmtData,
  fmtInt,
  labelSituacao,
} from "../../ConferenciaFatura/formatters";
import {
  META_PADRAO_DIAS,
  SITUACAO_INTEGRADA,
  SITUACOES_EM_CURSO,
  calcularAging,
  calcularAnalise,
  dataEmissao,
  medirCiclos,
  somarEmCurso,
} from "./estatisticas";
import { fmtBRLInteiro } from "./formatos";
import {
  baixarExportXlsx,
  buscarAging,
  buscarExecutores,
  buscarMotivos,
  buscarResumo,
  buscarVolume,
  mensagemErro,
  mensagemErroBlob,
  montarParams,
} from "./leadTimeApi";
import JanelaAuditoria from "./JanelaAuditoria";
import TempoPorSituacao from "./TempoPorSituacao";
import VolumePeriodo from "./VolumePeriodo";
import Executores from "./Executores";
import MotivosBloqueio from "./MotivosBloqueio";
import AgingSituacao from "./AgingSituacao";
import "./styles.css";

// O endpoint de conferência é paginado. Puxamos em lotes até o teto para
// calcular as estatísticas no cliente sem precisar de endpoint novo. 1000 é o
// máximo aceito pelo Yup do controller; 10 páginas = teto de 10.000 faturas.
const PAGE_SIZE = 1000;
const MAX_PAGINAS = 10;

const META_STORAGE_KEY = "@HubEverlog:leadTimeFaturaMeta";

const QUICK_RANGES = [
  { key: "month", label: "Mês atual", days: null },
  { key: "90d", label: "90 dias", days: 89 },
  { key: "180d", label: "180 dias", days: 179 },
  { key: "365d", label: "12 meses", days: 364 },
];

const getQuickRangeDates = (rangeKey) => {
  const hoje = moment();
  if (rangeKey === "month") {
    return {
      desde: hoje.clone().startOf("month").format("YYYY-MM-DD"),
      ate: hoje.format("YYYY-MM-DD"),
    };
  }
  const range = QUICK_RANGES.find((item) => item.key === rangeKey);
  const days = range && range.days != null ? range.days : 89;
  return {
    desde: hoje.clone().subtract(days, "days").format("YYYY-MM-DD"),
    ate: hoje.format("YYYY-MM-DD"),
  };
};

const createDefaultFilters = () => getQuickRangeDates("90d");

const lerMetaSalva = () => {
  try {
    const bruto = localStorage.getItem(META_STORAGE_KEY);
    const num = Number(bruto);
    return bruto !== null && Number.isFinite(num) && num > 0
      ? num
      : META_PADRAO_DIAS;
  } catch (e) {
    return META_PADRAO_DIAS;
  }
};

const fmtDias = (v) =>
  v === null || v === undefined
    ? "-"
    : `${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} d`;

const fmtDiasInteiro = (v) =>
  v === null || v === undefined ? "-" : `${fmtInt(v)} d`;

const fmtPct = (v) =>
  v === null || v === undefined
    ? "-"
    : `${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

const fmtDiasSinalizado = (v) => {
  if (v === null || v === undefined) return "-";
  const num = Number(v);
  return `${num > 0 ? "+" : ""}${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} d`;
};

// Estado inicial de cada seção servida por endpoint próprio (PO1-3000/3001).
// `data` e `error` convivem: a seção mostra o erro DELA sem apagar as outras.
const SECAO_INICIAL = { data: null, error: null, loading: false };

const LeadTimeFatura = () => {
  const { empresa, transportadoraId, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const empresaId = empresa && empresa.value;

  const [filtros, setFiltros] = useState(createDefaultFilters);
  const filtrosRef = useRef(filtros);
  const [meta, setMeta] = useState(lerMetaSalva);
  const [concluidas, setConcluidas] = useState([]);
  const [emCurso, setEmCurso] = useState([]);
  const [anterioresIgnoradas, setAnterioresIgnoradas] = useState(null);
  const [truncado, setTruncado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consultado, setConsultado] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [transportadoraFoco, setTransportadoraFoco] = useState(null);

  // Seções servidas pelo backend (H2/H3). Cada uma tem estado próprio para que
  // uma falha isolada não derrube a tela inteira.
  const [resumoLT, setResumoLT] = useState(SECAO_INICIAL);
  const [volumeLT, setVolumeLT] = useState(SECAO_INICIAL);
  const [executoresLT, setExecutoresLT] = useState(SECAO_INICIAL);
  const [motivosLT, setMotivosLT] = useState(SECAO_INICIAL);
  const [agingLT, setAgingLT] = useState(SECAO_INICIAL);
  const [granularidade, setGranularidade] = useState("dia");
  const granularidadeRef = useRef(granularidade);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    filtrosRef.current = filtros;
  }, [filtros]);

  useEffect(() => {
    granularidadeRef.current = granularidade;
  }, [granularidade]);

  useEffect(() => {
    try {
      localStorage.setItem(META_STORAGE_KEY, String(meta));
    } catch (e) {
      // Preferência é conveniência: se o storage falhar, segue com o valor da sessão.
    }
  }, [meta]);

  const buscarPaginado = useCallback(async (paramsBase) => {
    const items = [];
    let totalCount = 0;
    for (let page = 1; page <= MAX_PAGINAS; page += 1) {
      const res = await api.get("/faturas/conferencia", {
        params: { ...paramsBase, page, pageSize: PAGE_SIZE },
      });
      const lote = (res.data && res.data.items) || [];
      totalCount = (res.data && res.data.totalCount) || totalCount;
      items.push(...lote);
      if (lote.length < PAGE_SIZE || items.length >= totalCount) break;
    }
    return { items, totalCount };
  }, []);

  // Roda UMA seção do backend. A promessa nunca é repassada adiante: o erro
  // vira estado da própria seção, nunca exceção do carregamento da tela.
  const carregarSecao = useCallback((setter, promessa, fallback) => {
    setter((atual) => ({ ...atual, loading: true, error: null }));
    return promessa
      .then((data) => setter({ data, error: null, loading: false }))
      .catch((requestError) =>
        setter({
          data: null,
          error: mensagemErro(requestError, fallback),
          loading: false,
        })
      );
  }, []);

  // O volume recarrega sozinho na troca de granularidade — sem refazer as
  // outras cinco chamadas.
  const carregarVolume = useCallback(
    (filtrosAtivos, gran) => {
      if (!empresaId || !hasEmpresaSelection) return Promise.resolve();
      const params = montarParams({
        desde: filtrosAtivos.desde,
        ate: filtrosAtivos.ate,
        idTransportadora: transportadoraId,
      });
      return carregarSecao(
        setVolumeLT,
        buscarVolume(params, gran),
        "Erro ao carregar o volume por período."
      );
    },
    [empresaId, hasEmpresaSelection, transportadoraId, carregarSecao]
  );

  const carregar = useCallback(
    async (filtrosAtivos) => {
      if (!empresaId || !hasEmpresaSelection) return;
      if (!filtrosAtivos.desde || !filtrosAtivos.ate) {
        setError('Informe o período ("De" e "Até").');
        return;
      }
      if (filtrosAtivos.desde > filtrosAtivos.ate) {
        setError("A data inicial não pode ser maior que a data final.");
        return;
      }

      setLoading(true);
      setError(null);
      setTransportadoraFoco(null);

      // Seções do backend (H2/H3): disparadas em paralelo com as consultas da
      // H1 e deliberadamente NÃO aguardadas aqui — cada uma resolve o próprio
      // estado, então um endpoint novo fora do ar não derruba a tela.
      const paramsLT = montarParams({
        desde: filtrosAtivos.desde,
        ate: filtrosAtivos.ate,
        idTransportadora: transportadoraId,
      });
      carregarSecao(
        setResumoLT,
        buscarResumo(paramsLT),
        "Erro ao carregar a janela da auditoria."
      );
      carregarVolume(filtrosAtivos, granularidadeRef.current);
      carregarSecao(
        setExecutoresLT,
        buscarExecutores(paramsLT),
        "Erro ao carregar os executores das transições."
      );
      carregarSecao(
        setMotivosLT,
        buscarMotivos(paramsLT),
        "Erro ao carregar os motivos de bloqueio."
      );
      carregarSecao(
        setAgingLT,
        buscarAging(paramsLT),
        "Erro ao carregar o aging por situação."
      );

      try {
        const comuns = {};
        if (transportadoraId) comuns.idTransportadora = transportadoraId;

        const [ciclosFechados, backlog, counts] = await Promise.all([
          // Ciclos concluídos: filtra pela data de INTEGRAÇÃO no período.
          buscarPaginado({
            ...comuns,
            dataCampo: "integracao",
            dataDe: filtrosAtivos.desde,
            dataAte: filtrosAtivos.ate,
            idSituacoes: String(SITUACAO_INTEGRADA),
            mostrarFinalizadas: 1,
          }),
          // Backlog: o que ainda está na esteira e ENTROU no período. Sem esse
          // recorte a consulta traz o passivo histórico inteiro (estoura o teto
          // e o envelhecimento passa a medir faturas de anos anteriores).
          buscarPaginado({
            ...comuns,
            dataCampo: "entrada",
            dataDe: filtrosAtivos.desde,
            dataAte: filtrosAtivos.ate,
            idSituacoes: SITUACOES_EM_CURSO.join(","),
            mostrarFinalizadas: 0,
          }),
          // Contagem total das situações em curso (o /resumo ignora o filtro de
          // situação e de data): serve só para dizer quantas faturas em aberto
          // ficaram de fora por terem entrado antes do período. Se falhar, a
          // tela segue sem o rodapé.
          api
            .get("/faturas/conferencia/resumo", {
              params: { ...comuns, mostrarFinalizadas: 0 },
            })
            .then((res) => (res.data && res.data.counts) || null)
            .catch(() => null),
        ]);

        setConcluidas(ciclosFechados.items);
        setEmCurso(backlog.items);
        setAnterioresIgnoradas(
          counts ? Math.max(0, somarEmCurso(counts) - backlog.totalCount) : null
        );
        setTruncado(
          ciclosFechados.items.length < ciclosFechados.totalCount ||
            backlog.items.length < backlog.totalCount
        );
        setConsultado(true);
      } catch (requestError) {
        const msg =
          (requestError.response &&
            requestError.response.data &&
            requestError.response.data.error) ||
          requestError.message ||
          "Erro ao carregar o lead time de fatura.";
        setConcluidas([]);
        setEmCurso([]);
        setAnterioresIgnoradas(null);
        setError(msg);
        setConsultado(true);
        addToast({
          place: "tr",
          type: "danger",
          title: "Lead Time de Fatura",
          description: msg,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      empresaId,
      hasEmpresaSelection,
      transportadoraId,
      buscarPaginado,
      addToast,
      carregarSecao,
      carregarVolume,
    ]
  );

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setConcluidas([]);
      setEmCurso([]);
      setAnterioresIgnoradas(null);
      setError(null);
      setConsultado(false);
      setResumoLT(SECAO_INICIAL);
      setVolumeLT(SECAO_INICIAL);
      setExecutoresLT(SECAO_INICIAL);
      setMotivosLT(SECAO_INICIAL);
      setAgingLT(SECAO_INICIAL);
      return;
    }
    carregar(filtrosRef.current);
  }, [empresaId, hasEmpresaSelection, carregar]);

  const applyQuickRange = (key) => {
    const next = getQuickRangeDates(key);
    setFiltros(next);
    carregar(next);
  };

  const trocarGranularidade = (key) => {
    setGranularidade(key);
    carregarVolume(filtrosRef.current, key);
  };

  // Registros com ciclo mensurável: precisam de emissão E integração. Os sem
  // data ficam de fora da estatística e são reportados no rodapé — não dá para
  // medir o que não tem as duas pontas.
  const registros = useMemo(() => medirCiclos(concluidas), [concluidas]);

  const semMedicao = concluidas.length - registros.length;

  const analise = useMemo(() => calcularAnalise(registros, meta), [
    registros,
    meta,
  ]);

  // Backlog por faixa de idade: onde o dinheiro está parado hoje.
  const aging = useMemo(
    () => calcularAging(emCurso, meta, moment().format("YYYY-MM-DD")),
    [emCurso, meta]
  );

  const detalhe = useMemo(() => {
    const base = transportadoraFoco
      ? registros.filter(
          (r) => (r.transportadoraFantasia || "—") === transportadoraFoco
        )
      : registros;
    return [...base].sort((a, b) => b.leadTime - a.leadTime);
  }, [registros, transportadoraFoco]);

  // Export XLSX gerado no backend (PO1-3000): 7 abas com o indicador inteiro —
  // resumo, por situação, faturas, volume, executores, motivos e aging. Substitui
  // o CSV client-side da H1, que só sabia exportar o detalhamento da tela.
  const handleExport = async () => {
    if (!empresaId) return;
    setExportando(true);
    try {
      const blob = await baixarExportXlsx(
        montarParams({
          desde: filtros.desde,
          ate: filtros.ate,
          idTransportadora: transportadoraId,
        }),
        granularidade
      );
      fileDownload(
        blob,
        `lead-time-fatura-${filtros.desde}-a-${filtros.ate}.xlsx`
      );
    } catch (requestError) {
      // O 400 do teto de 50.000 faturas vem como JSON DENTRO do blob: sem ler o
      // corpo a tela esconderia justamente a instrução ("Refine os filtros").
      const msg = await mensagemErroBlob(
        requestError,
        "Falha ao gerar a planilha."
      );
      addToast({
        place: "tr",
        type: "danger",
        title: "Exportar XLSX",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setExportando(false);
    }
  };

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice
          title="Selecione um embarcador no cabeçalho"
          message="Este indicador usa o embarcador selecionado no topo para medir o ciclo de liberação das faturas de frete."
        />
      </div>
    );
  }

  const activeRange = QUICK_RANGES.find((range) => {
    const r = getQuickRangeDates(range.key);
    return r.desde === filtros.desde && r.ate === filtros.ate;
  });
  const periodoLabel =
    filtros.desde && filtros.ate
      ? `${moment(filtros.desde).format("DD/MM/YYYY")} a ${moment(
          filtros.ate
        ).format("DD/MM/YYYY")}`
      : "";

  const temResultado = consultado && !error && analise.qtd > 0;
  const vazio = consultado && !error && analise.qtd === 0;
  const metaTone =
    analise.dentroDaMeta === null
      ? "neutral"
      : analise.dentroDaMeta >= 80
      ? "success"
      : analise.dentroDaMeta >= 50
      ? "warning"
      : "pink";

  const colunasDetalhe = [
    { dataField: "numFaturaExterna", text: "Nº fatura", formatter: (c, r) => c || r.id },
    { dataField: "transportadoraFantasia", text: "Transportadora", formatter: (c) => c || "—" },
    {
      dataField: "situacaoCodigo",
      text: "Situação",
      formatter: (c, r) => labelSituacao(c, r.situacaoNome),
    },
    {
      dataField: "dtEmissao",
      text: "Dt. emissão",
      formatter: (c) => fmtData(c) || "-",
    },
    {
      dataField: "dataPagamento",
      text: "Dt. integração",
      formatter: (c) => fmtData(c) || "-",
    },
    {
      dataField: "leadTime",
      text: "Lead time",
      align: "right",
      headerAlign: "right",
      formatter: (c) => (
        <span className={`lt-badge lt-badge--${c > meta ? "atraso" : "ok"}`}>
          {fmtDiasInteiro(c)}
        </span>
      ),
    },
    {
      dataField: "divergencia",
      text: "Divergência",
      formatter: (c) => (c ? "Sim" : "Não"),
    },
    {
      dataField: "valTotalFatura",
      text: "Valor",
      align: "right",
      headerAlign: "right",
      formatter: (c) => fmtBRL(c),
    },
  ];

  const dadosDetalhe = detalhe.map((r) => ({
    ...r,
    dtEmissao: dataEmissao(r),
  }));

  // O backlog passou a ser recortado pelo periodo (PO1-2422): um periodo sem
  // nenhuma fatura integrada ainda pode ter faturas em aberto para mostrar.
  // Por isso o card sai do ramo "temResultado" e e renderizado tambem no vazio.
  const cardBacklog = (
    <ShellCard
      eyebrow="Backlog"
      title="Faturas ainda na esteira"
      description="Faturas com entrada no período que ainda não integraram. Idade contada a partir da emissão."
      className="lt-panel"
    >
      {aging.total === 0 ? (
        <div className="lt-state">
          Nenhuma fatura com entrada no período segue em aberto.
        </div>
      ) : (
        <>
          <div className="lt-aging-resumo">
            <div>
              <span className="lt-aging-resumo__label">Em aberto</span>
              <strong>{fmtInt(aging.total)}</strong>
            </div>
            <div>
              <span className="lt-aging-resumo__label">Valor parado</span>
              <strong>{fmtBRL(aging.valorTotal)}</strong>
            </div>
            <div>
              <span className="lt-aging-resumo__label">Idade média</span>
              <strong>{fmtDias(aging.idadeMedia)}</strong>
            </div>
            <div>
              <span className="lt-aging-resumo__label">
                Acima da meta
              </span>
              <strong>{fmtInt(aging.acimaDaMeta)}</strong>
            </div>
          </div>
          <div className="lt-table-wrap">
            <Table className="lt-table" responsive>
              <thead>
                <tr>
                  <th>Faixa</th>
                  <th className="lt-num">Faturas</th>
                  <th className="lt-num">Valor</th>
                </tr>
              </thead>
              <tbody>
                {aging.faixas.map((faixa) => (
                  <tr key={faixa.key}>
                    <td>
                      <span
                        className={`lt-dot lt-dot--${faixa.tone}`}
                      />
                      {faixa.label}
                    </td>
                    <td className="lt-num">{fmtInt(faixa.qtd)}</td>
                    <td className="lt-num">{fmtBRL(faixa.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {aging.semData ? (
            <p className="lt-footnote">
              {fmtInt(aging.semData)} fatura(s) em aberto sem data de
              emissão ficaram fora do envelhecimento.
            </p>
          ) : null}
        </>
      )}
      {anterioresIgnoradas ? (
        <p className="lt-footnote">
          {fmtInt(anterioresIgnoradas)} fatura(s) em aberto com
          entrada anterior ao período não entram no backlog. Amplie o
          período para vê-las.
        </p>
      ) : null}
    </ShellCard>
  );

  return (
    <div className="content lt-page">
      <PageHero
        eyebrow="Indicadores"
        title="Lead Time de Fatura"
        description="Da emissão da fatura pela transportadora até a integração com o ERP (a Dt. Integração da Auditoria de Faturas). Não é a data do pagamento bancário."
        actions={
          <>
            <PageHeroRefreshButton
              onClick={() => carregar(filtros)}
              loading={loading}
            />
            <PageHeroExportButton
              label="Exportar XLSX"
              onClick={handleExport}
              loading={exportando}
              disabled={loading}
            />
          </>
        }
      />

      <ShellCard>
        <div className="lt-busca">
          <div className="lt-busca__label">
            <span className="lt-busca__title">Período de integração</span>
            {periodoLabel ? (
              <span className="lt-busca__hint">
                {periodoLabel}
                {!activeRange ? " (personalizado)" : ""}
              </span>
            ) : null}
          </div>
          <div className="lt-quick-ranges">
            {QUICK_RANGES.map((range) => (
              <button
                key={range.key}
                type="button"
                className={`lt-quick-range${
                  activeRange && activeRange.key === range.key ? " is-active" : ""
                }`}
                disabled={loading}
                onClick={() => applyQuickRange(range.key)}
              >
                {range.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={`lt-busca__filtros${mostrarFiltros ? " is-open" : ""}`}
            onClick={() => setMostrarFiltros((v) => !v)}
            aria-expanded={mostrarFiltros}
          >
            <i className="tim-icons icon-settings-gear-63" />
            <span>Filtros</span>
          </button>
        </div>

        {mostrarFiltros ? (
          <section className="lt-advfilters">
            <Row className="align-items-end">
              <Col lg="3" md="6">
                <FormGroup>
                  <Label>De</Label>
                  <Input
                    type="date"
                    value={filtros.desde}
                    disabled={loading}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setFiltros((atual) => ({ ...atual, desde: valor }));
                    }}
                  />
                </FormGroup>
              </Col>
              <Col lg="3" md="6">
                <FormGroup>
                  <Label>Até</Label>
                  <Input
                    type="date"
                    value={filtros.ate}
                    disabled={loading}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setFiltros((atual) => ({ ...atual, ate: valor }));
                    }}
                  />
                </FormGroup>
              </Col>
              <Col lg="3" md="6">
                <FormGroup>
                  <Label>Meta de ciclo (dias)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={meta}
                    disabled={loading}
                    onChange={(e) => {
                      const num = Number(e.target.value);
                      if (Number.isFinite(num) && num > 0) setMeta(num);
                    }}
                  />
                </FormGroup>
              </Col>
              <Col lg="3" md="6" className="lt-advfilters__actions">
                <Button color="info" disabled={loading} onClick={() => carregar(filtros)}>
                  {loading ? "Carregando..." : "Consultar"}
                </Button>
              </Col>
            </Row>
          </section>
        ) : null}
      </ShellCard>

      {loading ? (
        <div className="lt-loading">
          <ClipLoader color={AUTOPILOT_COLORS.title} size={32} />
        </div>
      ) : error ? (
        <ShellCard>
          <div className="lt-state lt-state--error">{error}</div>
        </ShellCard>
      ) : (
        <>
          {/* H2/H3: seções servidas pelo backend. Ficam FORA do ramo
              "temResultado" de propósito — um período sem ciclo concluído ainda
              tem janela da auditoria, volume, motivos e aging para mostrar. */}
          <JanelaAuditoria estado={resumoLT} />
          <TempoPorSituacao estado={resumoLT} />

          {temResultado ? (
            <>
              <MetricCardGroup
                eyebrow="Ciclo concluído"
                title="Faturas integradas no período"
                description={`Da emissão até a integração no ERP. Meta atual: ${fmtInt(
                  meta
                )} dias.`}
              >
                <MetricCard
                  label="Ciclo médio"
                  tone="info"
                  iconClassName="tim-icons icon-time-alarm"
                  value={fmtDias(analise.cicloMedio)}
                  helper={`${fmtInt(analise.qtd)} fatura(s) medida(s)`}
                />
                <MetricCard
                  label="Mediana"
                  tone="neutral"
                  iconClassName="tim-icons icon-chart-bar-32"
                  value={fmtDias(analise.mediana)}
                  helper="Metade das faturas fecha em até esse prazo"
                />
                <MetricCard
                  label="P90"
                  tone="warning"
                  iconClassName="tim-icons icon-alert-circle-exc"
                  value={fmtDias(analise.p90)}
                  helper="9 em cada 10 faturas fecham em até esse prazo"
                />
                <MetricCard
                  label="Dentro da meta"
                  tone={metaTone}
                  iconClassName="tim-icons icon-check-2"
                  value={fmtPct(analise.dentroDaMeta)}
                  helper={`Faturas integradas em até ${fmtInt(meta)} dias`}
                />
                <MetricCard
                  label="Custo da divergência"
                  tone={
                    analise.custoDaDivergencia !== null &&
                    analise.custoDaDivergencia > 0
                      ? "pink"
                      : "success"
                  }
                  iconClassName="tim-icons icon-alert-circle-exc"
                  value={fmtDiasSinalizado(analise.custoDaDivergencia)}
                  helper={`${fmtInt(
                    analise.qtdComDiv
                  )} fatura(s) com divergência vs. ciclo das sem`}
                />
                <MetricCard
                  label="Valor integrado"
                  tone="success"
                  iconClassName="tim-icons icon-money-coins"
                  value={fmtBRLInteiro(analise.valorTotal)}
                  helper="Total das faturas medidas no período"
                />
              </MetricCardGroup>

              <div className="lt-panels">
                <ShellCard
                  eyebrow="Ranking"
                  title="Transportadoras com o pior ciclo"
                  description="Clique para filtrar o detalhamento abaixo."
                  className="lt-panel"
                >
                  {analise.ranking.length === 0 ? (
                    <div className="lt-state">
                      Sem transportadoras no período.
                    </div>
                  ) : (
                    <ul className="lt-rank">
                      {analise.ranking.map((item, index) => {
                        const ativo = transportadoraFoco === item.nome;
                        return (
                          <li
                            key={item.nome}
                            className={`lt-rank__item${ativo ? " is-active" : ""}`}
                            onClick={() =>
                              setTransportadoraFoco(ativo ? null : item.nome)
                            }
                            title={
                              ativo
                                ? "Remover filtro do detalhamento"
                                : "Filtrar o detalhamento por esta transportadora"
                            }
                          >
                            <span className="lt-rank__pos">{index + 1}</span>
                            <span className="lt-rank__nome">
                              {item.nome}
                              <span className="lt-rank__qtd">
                                {fmtInt(item.qtd)} fatura(s) · {fmtBRL(item.valor)}
                              </span>
                            </span>
                            <span
                              className={`lt-rank__valor${
                                item.ciclo > meta ? " is-atraso" : ""
                              }`}
                            >
                              {fmtDias(item.ciclo)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </ShellCard>

                {cardBacklog}
              </div>

              <ShellCard
                eyebrow="Detalhe"
                title="Faturas integradas no período"
                description="Ordenadas do maior para o menor ciclo — o topo é onde atacar primeiro."
              >
                {transportadoraFoco ? (
                  <div className="lt-drill-filter">
                    <span>
                      Filtrado por <strong>{transportadoraFoco}</strong>
                    </span>
                    <button
                      type="button"
                      className="lt-drill-filter__clear"
                      onClick={() => setTransportadoraFoco(null)}
                    >
                      limpar filtro ✕
                    </button>
                  </div>
                ) : null}
                <CustomTable
                  data={dadosDetalhe}
                  columns={colunasDetalhe}
                  sizePerPage={10}
                  showToolbar={false}
                  themedPagination
                  tableClasses="text-left"
                />
                {semMedicao ? (
                  <p className="lt-footnote">
                    {fmtInt(semMedicao)} fatura(s) integrada(s) no período ficaram
                    fora do cálculo por não ter data de emissão ou de integração.
                  </p>
                ) : null}
                {truncado ? (
                  <p className="lt-footnote">
                    O volume do período excede o teto de{" "}
                    {fmtInt(PAGE_SIZE * MAX_PAGINAS)} faturas por consulta —
                    vale tanto para os ciclos concluídos quanto para o backlog.
                    Estreite o período para medir o recorte completo.
                  </p>
                ) : null}
              </ShellCard>
            </>
          ) : vazio ? (
            <>
              <ShellCard>
                <div className="lt-state">
                  Nenhuma fatura integrada no período com data de emissão e de
                  integração preenchidas. Ajuste o período ou confira as faturas
                  na Auditoria de Faturas.
                </div>
              </ShellCard>
              {cardBacklog}
            </>
          ) : null}

          <VolumePeriodo
            estado={volumeLT}
            granularidade={granularidade}
            onGranularidade={trocarGranularidade}
          />
          <MotivosBloqueio estado={motivosLT} />
          <Executores estado={executoresLT} />
          <AgingSituacao estado={agingLT} />
        </>
      )}
    </div>
  );
};

export default LeadTimeFatura;
